<template>
  <div class="mdnComponent">
    <v-jstree :data="internalData" :draggable="draggable"
              :item-events="itemEvents"
              :drag-over-background-color="dragBackgroundColor"
              @item-click="itemClick"
              @item-toggle="itemToggle"
              @item-drag-start="itemDragStart"
              @item-drag-end="itemDragEnd"
              @item-drop="onItemDrop">
      <template slot-scope="_">
        <div style="display: inherit; width: 100%" :class="setTextColor(_.model)">
          <i :class="_.vm.themeIconClasses" role="presentation"></i>
          <i class="tree-icon tree-themeicon tree-themeicon-custom" :class="_.model.secondIcon" v-if="!!_.model.secondIcon" role="presentation"></i>
          {{_.model.text}}
        </div>
      </template>
    </v-jstree>
    <v-menu v-model="menuVisible" :position-x="menuX" :position-y="menuY" absolute offset-y>
      <v-list dense class="menuitem">
        <v-list-tile ripple v-for="(item, index) in menuEntries" :key="index" @click="" class="menutile">
          <v-list-tile-title @click="handleMenuClick(item)">{{ item.title }}</v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
  /** known issues:
   *  - Edge: Drag/Drop Problem, not working properly
   *  - non-webkit-browsers: scrollbar styling is not working
   *  - drop and move operations only update parent_id and do not recognize or update _path and _pathname
   *  - node id's have to be uniqe!!
   *  - autosave / restore expanded state not implemented yet
   *  - treelines invisible in dark theming bc. base64 encoded image from jstree has black lines
   *  - dragDisabled / dropDisabled is working (isdir = drop is enabled, m-flag = drag is enabled). but canDrop-Method and cursor-change is not implemented
   *
   *  prerequirements:
   *  - project where MdnTreeView is used should feature vue, vuetify, vue-jstree and fontawesome.
   */
  export default {
    props: {
      treedata: { //data coming from extern, not to change from us because of reasons!!
        type: Array,
        default: []
      },
      verbose: {  //send change events for some outside component to update or not
        type: Boolean,
        default: false
      },
      dragBackgroundColor: { //determines which color the drag-drop highlight color will be
        type: String,
        default: '#aaa'
      },
      sortNodes: {  //per default, every child-collection is sorted alphabetical and folders are sorted in front in every node.
        type: Boolean,
        default: true
      },
      expandAll: {
        type: Boolean,
        default: false
      }
    },
    data: function() {
      let me = this
      return {
        internalData: [], //for internal use only
        externalData: [], //for external state checks
        itemEvents: {
          contextmenu: function (node, nodeItem, e) { //invoked by vuejs tree on context-click, opens vuetify contextmenu
            e.preventDefault()
            e.stopPropagation()
            if (!nodeItem) return;
            me.menuItem = nodeItem;
            if(nodeItem._cmds) {
              me.menuEntries = []
              for (let i = 0; i < nodeItem._cmds.length; i++) {
                me.menuEntries.push({title: nodeItem._cmds[i].replace('ws-$pcat-','').replace('§',''), command: nodeItem._cmds[i]})
              }
              me.menuX = e.clientX;
              me.menuY = e.clientY;
              me.$nextTick(() => {
                me.menuVisible = true;
              })
            }
          }
        },
        draggable: true,
        toggleCount: [], //vuejstree toggle event bug fires twice, this is to filter second event and expose just one of them
        menuVisible: false, //data for contextmenu starts here...
        menuX: 0, //positionX of menu to be
        menuY: 0, //positionY of menu to be
        menuItem: null, //the clicked Treenode for the contextmenu
        menuEntries: [] //menuentries bound to the vuetify menu, determined on contextmenu click event from clicked treenode. command-property is name of global function with node-param
      }
    },
    name: 'MdnTreeView',
    methods: {
      notifyDataChanged() { //if tree is in verbose state, every action to trigger an internal data-change emits the changed treedata up to parent
        if(this.verbose === true) {
          this.externalData = this.makeDataFlat(this.internalData)
          let vm = this
          vm.$nextTick(function () {
            vm.$emit('datachanged', vm.externalData)
          })
        }
      },
      makeDataHierarchical(data) { //process flat data from outside to be consumable by vuejs tree and mdntree abstraction layer
        let hierarchicalTmpArray = []
        for(let i = 0; i < data.length; i++) {
          let entry = data[i]
          let newEntry = {}
          for (let property in entry) {
            let propName = property
            let propVal = entry[property]
            if(property == 'name') {
              propName = 'text'
              newEntry['value'] = propVal
            }
            if(property == '_id') propName = 'id'
            if(property == 'isdir') {
              if(propVal == true) {
                newEntry['icon'] = 'fas fa-folder'
                newEntry.dropDisabled = false
              }
              else {
                newEntry['icon'] = 'fas fa-file-alt'
                newEntry.dropDisabled = true
              }
              newEntry.isLeaf = !propVal
            }
            if(entry._elock != null) newEntry.icon = "fas fa-lock icon-state-danger"
            if(entry.flags != null && entry.flags.e == 1) newEntry.editable = true
            newEntry[propName] = propVal
          }

          if(entry.notify != null && entry.notify == 'warning') newEntry.secondIcon = 'fas fa-exclamation-triangle icon-state-warning'
          if(entry.notify != null && entry.notify == 'alert') newEntry.secondIcon = 'fas fa-exclamation-triangle icon-state-danger'

          if(entry.flags != null && entry.flags.m == 1) {
            if(!entry.isdir && entry.flags.e != 1) newEntry.dragDisabled = true //if not a directory, file must be editable AND managable to be draggable
            else newEntry.dragDisabled = false
          }
          else newEntry.dragDisabled = true

          newEntry.children = []
          newEntry.opened = !!entry.expanded ? entry.expanded : false
          newEntry.selected = false
          newEntry.disabled = false
          newEntry.order = i
          newEntry.bgColor = "inherit"
          hierarchicalTmpArray.push(newEntry)
        }
        let hierarchicalArray = []
        for(let i = 0; i < hierarchicalTmpArray.length; i++) {
          if(hierarchicalTmpArray[i].parent_id != null) {
            for(let j = 0; j < hierarchicalTmpArray.length; j++) {
              if(hierarchicalTmpArray[i].parent_id == hierarchicalTmpArray[j].id) {
                hierarchicalTmpArray[j].children.push(hierarchicalTmpArray[i])
                hierarchicalTmpArray[i].parent = hierarchicalTmpArray[j];
                hierarchicalTmpArray[i]._parented = true
              }
            }
          }
        }
        for(let i = 0; i < hierarchicalTmpArray.length; i++) {
          let node = hierarchicalTmpArray[i]
          while(node.parent != undefined) {
            node = node.parent
          }
          hierarchicalTmpArray[i].rootId = node.id
        }
        for(let i = 0; i < hierarchicalTmpArray.length; i++) {
          if(hierarchicalTmpArray[i]._parented != true) {
            hierarchicalArray.push(hierarchicalTmpArray[i])
          }
          else delete hierarchicalTmpArray[i]._parented
          delete hierarchicalTmpArray[i].parent
        }
        this.recursiveSortHierarchicalData(hierarchicalArray)
        return hierarchicalArray
      },
      backProcessItem(internalItem, delOrder) { //backwards processing of single item (for context-menu invoking and generating source schema of internal data)
        delete internalItem.children
        internalItem.expanded = internalItem.opened
        delete internalItem.opened
        delete internalItem.selected
        delete internalItem.disabled
        //internalItem.isdir = !internalItem.isLeaf
        delete internalItem.isLeaf
        delete internalItem.icon
        internalItem._id = internalItem.id
        delete internalItem.id
        internalItem.name = internalItem.text
        delete internalItem.text
        delete internalItem.value
        delete internalItem.dropDisabled
        delete internalItem.dragDisabled
        delete internalItem.icon
        delete internalItem.editable
        delete internalItem.bgColor
        delete internalItem.rootId
        delete internalItem.secondIcon
        delete internalItem.loading
        if(delOrder) delete internalItem.order
      },
      makeDataFlat(data) { //backwards processing of internal data to source schema
        let data2 = JSON.parse(JSON.stringify(data)) //deep copy
        let flatArray = []
        let crawl = function(item, parentId) {
          if(parentId != null) {
            item.parent_id = parentId
          }
          this.push(item)
          for(let i = 0; i < item.children.length; i++) {
            let entry = item.children[i]
            crawl(entry, item.id)
          }
        }.bind(flatArray)
        for(let i = 0; i < data2.length; i++) {
          let entry = data2[i]
          crawl(entry)
        }
        for(let i = 0; i < flatArray.length; i++) {
          var entry = flatArray[i]
          this.backProcessItem(entry, false)
        }
        let flatArray2 = [];
        for(let i = 0; i < flatArray.length; i++) {
          flatArray2.push({})
        }
        for(let i = 0; i < flatArray.length; i++) {
          flatArray2[flatArray[i].order] = flatArray[i]
          delete flatArray[i].order
        }
        return flatArray2
      },
      recursiveSortHierarchicalData(data, sortNextLevel) { //sort treenodes and sections accordingly to sort logic
        data.sort(function (a, b) {
          if(sortNextLevel) {
            if (!a.isdir && !b.isdir || a.isdir && b.isdir) {
              return ('' + a.text).localeCompare(b.text);
            } else if (!a.isdir) return 1
            else return -1
          } else {
            if(!!a.sortidx && !!b.sortidx) return  a.sortidx - b.sortidx
            else return a.order - b.order
          }
        })
        for(let i = 0; i < data.length; i++) {
          if(data[i].children && data[i].children.length > 0) {
            this.recursiveSortHierarchicalData(data[i].children, this.sortNodes)
          }
        }
      },
      crawlChildrenForId(treeNode, idToFind) {
        if(treeNode.children.find(x => x.id == idToFind)) {
          return treeNode.id
        } else {
          for(let i = 0; i < treeNode.children.length; i++) {
            let result = this.crawlChildrenForId(treeNode.children[i], idToFind)
            if(result != -1) {
              return result;
            }
          }
          return -1
        }
      },
      findParentIdById(idToFind) {
        for(let i = 0; i < this.internalData.length; i++) {
          let result = this.crawlChildrenForId(this.internalData[i], idToFind)
          if(result != -1) return result
        }
        return -1
      },
      expandCollapseRecursively(data, expanded) {
        for(let i = 0; i < data.length; i++) {
          data[i].opened = expanded
          this.expandCollapseRecursively((data[i].children), expanded)
        }
      },
      createExposedNode(node) {
        let copy =JSON.parse(JSON.stringify(node))
        let parentId = this.findParentIdById(copy.id)
        copy.parent_id = parentId
        this.backProcessItem(copy, true)
        return copy
      },
      itemClick(node) { //item clickhandler invoked by jstree on node click
        let copy =JSON.parse(JSON.stringify(node.data))
        this.backProcessItem(copy, true)
        this.$emit('itemclick', copy, 'click', this, node.data)
        this.notifyDataChanged()
      },
      itemToggle(node) { //item clickhandler invoked by vuejstree on expand/collapse node tree
        if(this.toggleCount[node.data.id] != undefined && this.toggleCount[node.data.id] != 0) {
          this.toggleCount[node.data.id] = 0
        } else {
          this.toggleCount[node.data.id] = 1
          if (node.data.isdir) {
            this.$emit('itemclick', this.createExposedNode(node.data), node.data.opened?'expand':'collapse', this, node.data)
          }
        }
        this.notifyDataChanged()
      },
      itemDragEnd(node) { //treenode end drag handler invoked by vuejstree.
        this.notifyDataChanged()
      },
      itemDragStart(node) { //treenode start dragging handler invoked by vuejstree.
      },
      onItemDrop(oriNode, oriItem, droppedItem, e) { //treenode drop handler invoked by vuejstree. we need to sort our internal data after something is dropped on a node.

        let sortAfterDropSomethingAndCallCustomLogic = function() {
          this.recursiveSortHierarchicalData(this.internalData, false) //false, bc. first level is not going to be sorted
          //call custom logic:
          this.$emit('itemclick', this.createExposedNode(droppedItem), 'drop', this, droppedItem)
        }
        this.$nextTick(sortAfterDropSomethingAndCallCustomLogic)
        this.notifyDataChanged()
      },
      setTextColor(node) { //method to determine editable (green), non-editable (gray) or folder (black) color of treenode
        if(node.children == undefined || node.children.length == 0 || node.isLeaf == true) {
          if(node.editable == true)
          {
            return "mdnGreen"
          }
          else return "mdnGray"
        }
        else return "mdnBlack"
      },
      handleMenuClick(itemListEntry) { //invokes global function from clicking a treenode context menu item determined by _cmds
        let deepCopyObject = JSON.parse(JSON.stringify(this.menuItem)) //deep copy //create deepCopy of Object to backProcess without destroying internal data
        this.backProcessItem(deepCopyObject, true)
        this.$emit('itemclick', deepCopyObject, itemListEntry.command, this, this.menuItem)
      },
      generateGuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8)
          return v.toString(16)
        })
      },
    },
    watch: {
      treedata (newVal) { //listens to changes of outside data to restructure and rebuild internal data if changes happen
        this.internalData = this.makeDataHierarchical(newVal)
      },
      sortNodes(newVal) { //listens to outside prop for sorting behaviour of treenodes and restructures internal data accordingly
        this.recursiveSortHierarchicalData(this.internalData, false) //false bc first level is not going to be sorted
      },
      expandAll(newVal) {
        this.expandCollapseRecursively(this.internalData, newVal)
      }
    },
    mounted() { //initial data processing of outside data for internal use
      let vm = this
      vm.$nextTick(function () {
        vm.internalData = vm.makeDataHierarchical(vm.treedata)
      })
    }
  }
</script>

<style scoped>
  .mdnComponent {
    background: #222;
    border: none;
    height: 100%;
    overflow: auto;
    padding: 15px;
  }

  .mdnGreen {
    color: lightgreen !important;
  }
  .mdnBlack {
    color: #ddd;
  }
  .mdnGray {
    color: #6f6f6f;
  }
  .iconColor {
    color: black;
  }
  .icon-state-danger {
    color: red;
  }
  .icon-state-warning {
    color: yellow;
  }

  ::-webkit-scrollbar-track
  {
    -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
    background-color: #444;
  }

  ::-webkit-scrollbar
  {
    width: 12px;
    background-color: #F5F5F5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #000000;
    border: 0px solid #555555;
  }

  .theme--light.v-list {
    background: #ccc !important;
  }

  .menutile {
    height: 36px;
  }

  .menutile:hover {
    background: #666;
  }

  *:focus {
    outline: none;
  }

</style>
